import { DateTime } from "luxon";
import { useEffect } from "react";
import { useAuth } from "../../contexts/AuthProvider";
import { useNotificationsContext } from "../../contexts/NotificationsContext";
import { trackEvent } from "../../utils/analytics";

const SESSION_ID_STORAGE_KEY = "session_id";

/**
 * `notifications_page_viewed`, exactly as the Notifications page has always sent it: on open and again whenever the
 * list changes, with the user, a timestamp, the session, the list's counts, its notification types and the referrer.
 * Its reports depend on this shape, so the bell's Notifications tab tracks its own event instead.
 */
export const useNotificationsPageViewedTracking = (): void => {
	const { allNotifications } = useNotificationsContext();
	const { userInfo } = useAuth();
	const userId = userInfo?.id;

	useEffect(() => {
		if (!userId) return;
		const notificationTypeCounts: Record<string, number> = {};
		for (const notification of allNotifications) {
			const type = notification.notification_type;
			// DEFAULT: the first notification of a type starts its count.
			notificationTypeCounts[type] = (notificationTypeCounts[type] ?? 0) + 1;
		}
		trackEvent("notifications_page_viewed", {
			user_id: userId,
			timestamp: DateTime.utc().toISO(),
			// DEFAULT: "unknown" when no session id is stored — the value this event has always sent then.
			session_id: sessionStorage.getItem(SESSION_ID_STORAGE_KEY) || "unknown",
			total_notifications: allNotifications.length,
			unseen_notifications: allNotifications.filter((notification) => !notification.isSeen).length,
			clicked_notifications: allNotifications.filter((notification) => notification.isClicked).length,
			notification_types_distribution: notificationTypeCounts,
			// DEFAULT: "direct" for a visit with no referrer — the value this event has always sent then.
			referrer: document.referrer || "direct",
		});
	}, [userId, allNotifications]);
};
